/**
 * R2B · AS CONTAS A PAGAR E A RECEBER — uma conta só, para todas as telas.
 *
 * A revisão de 06/10/2026 achou o mesmo dinheiro com números diferentes em
 * cada tela: a ficha de Venda aparecia «a pagar» nas Fichas, no Pagamento, no
 * Custo e no Excel (só o Relatório acertava); a comissão do cliente nunca
 * virava «recebida»; o frete repartido dava um centavo de diferença entre o
 * Relatório e o Custo. Daqui em diante, QUEM DEVE O QUÊ sai desta função
 * (`contasDaFicha`), e as Fichas, a aba Pagamento, o Custo, a tela Contas, o
 * Relatório e o Excel de várias fichas só somam o que ela devolve. O teste
 * (`testes/financeiro.mjs`) monta uma ficha mista e uma venda e confere o
 * mesmo «a pagar» e «a receber» em todas.
 *
 * O QUE É CONTA (só ficha ENCERRADA — a aberta ainda não tem valor fechado;
 * a cancelada não deve nada):
 *   a pagar   · vendedor (compra): a parte do gestor do líquido (`divisaoDoPagamento`
 *               — a das porteiras Cliente o cliente paga direto);
 *             · cada frete ou gasto: a parte do gestor (`gastosDoGestor`); pago
 *               quando tem «pago em»;
 *             · Comissão do comprador (você paga): ao comprador da ficha (no
 *               acerto do extrato) ou a um corretor (fora do acerto);
 *   a receber · comprador (ficha de Venda): o líquido do romaneio — as
 *               quitações da ficha de Venda são os RECEBIMENTOS;
 *             · Comissão do cliente (você recebe), por porteira Cliente;
 *             · revenda: a venda ao comprador final, por porteira Revenda.
 *   A ficha de Conferência não entra em nenhum dos dois.
 *
 * Quem não vê comissão (comprador) recebe as contas sem as comissões e sem a
 * revenda — os dados nem estão no aparelho dele.
 *
 * Sem tela e sem armazenamento: só conta, em centavos inteiros.
 */
import { calcular, centavos, situacaoDePagamento, valorDaComissao, gastosValendo } from './contas'
import { contasPorPorteira, divisaoDoPagamento, gastosDoGestor, porteirasDa } from './destinos'
import { situacaoDoRecebimento } from './revenda'
import {
  NOME_COMISSAO_CLIENTE, NOME_COMISSAO_COMPRADOR,
  type Comissao, type ComissaoCliente, type Ficha, type PagamentoDeComissao, type Quitacao, type Recebimento, type VendaDaPorteira,
} from './tipos'

export type Sentido = 'pagar' | 'receber'
export type TipoDeConta = 'vendedor' | 'comprador' | 'gasto' | 'comissaoComprador' | 'comissaoCliente' | 'revenda'

export const NOMES_DAS_CONTAS: Record<TipoDeConta, string> = {
  vendedor: 'Vendedor',
  comprador: 'Comprador (venda)',
  gasto: 'Frete e gastos',
  comissaoComprador: NOME_COMISSAO_COMPRADOR,
  comissaoCliente: NOME_COMISSAO_CLIENTE,
  revenda: 'Revenda',
}

export interface ContaFinanceira {
  /** estável: `vendedor:<ficha>`, `gasto:<ficha>:<gasto>`, `comissao-cliente:<ficha>:<porteira>`… */
  id: string
  fichaId: string
  numero: string
  /** a data da ficha (o que não tem vencimento ordena por ela) */
  data: string
  sentido: Sentido
  tipo: TipoDeConta
  /** para quem se paga, ou de quem se recebe */
  quem: string
  descricao: string
  /** o que é devido no total */
  valor: number
  /** o que já foi pago (ou recebido) e vale */
  pago: number
  /** o que falta (nunca negativo) */
  falta: number
  /** pago (ou recebido) acima do devido */
  aMais: number
  /** vencimento combinado; nulo = sem data (a tela usa a data da ficha) */
  vencimento: string | null
  quitada: boolean
  destinoId?: string
  gastoId?: string
}

/** O que mora fora da ficha e mexe no «pago»: o armazém de pagamentos do aparelho. */
export interface DadosFinanceiros {
  quitacoes: Pick<Quitacao, 'fichaId' | 'valor' | 'anuladaEm' | 'recusada'>[]
  comissoes?: Record<string, Comissao>
  comissoesCliente?: Record<string, ComissaoCliente> | ComissaoCliente[]
  vendas?: Record<string, VendaDaPorteira> | VendaDaPorteira[]
  recebimentos?: Recebimento[]
  /** R2B · recebimentos da comissão do cliente (migração 014) */
  recebimentosComissao?: Recebimento[]
  /** R2B · pagamentos da comissão do comprador fora do acerto (migração 014) */
  pagamentosComissao?: PagamentoDeComissao[]
}

const emC = (v: number) => Math.round(v * 100)
const lista = <T,>(x: Record<string, T> | T[] | undefined): T[] => (Array.isArray(x) ? x : Object.values(x ?? {}))

/** Dinheiro que vale: nem anulado, nem recusado pelo servidor. */
function valendo<T extends { anuladoEm?: string | null; recusado?: string }>(xs: T[] | undefined): T[] {
  return (xs ?? []).filter((x) => !x.anuladoEm && !x.recusado)
}

/** O que já se pagou da comissão do comprador FORA do acerto (014). */
export function comissaoPagaFora(d: Pick<DadosFinanceiros, 'pagamentosComissao'>, fichaId: string): number {
  return centavos(valendo(d.pagamentosComissao).filter((x) => x.fichaId === fichaId).reduce((s, x) => s + x.valor, 0))
}

/** O que o cliente já pagou da comissão de uma porteira (014). */
export function comissaoRecebida(d: Pick<DadosFinanceiros, 'recebimentosComissao'>, fichaId: string, destinoId: string): number {
  return centavos(valendo(d.recebimentosComissao).filter((x) => x.fichaId === fichaId && x.destinoId === destinoId).reduce((s, x) => s + x.valor, 0))
}

function conta(base: Omit<ContaFinanceira, 'falta' | 'aMais' | 'quitada'>): ContaFinanceira {
  const v = emC(base.valor), p = emC(base.pago)
  return {
    ...base, valor: v / 100, pago: p / 100,
    falta: Math.max(0, v - p) / 100, aMais: Math.max(0, p - v) / 100,
    quitada: v > 0 && p >= v,
  }
}

/**
 * As contas de UMA ficha. `verComissao`: gestor e Consulta (o comprador não
 * vê comissão nem revenda). Ficha que não está encerrada, ou de Conferência:
 * nenhuma conta.
 */
export function contasDaFicha(f: Ficha, d: DadosFinanceiros, o: { verComissao: boolean }): ContaFinanceira[] {
  if (f.situacao !== 'encerrada') return []
  if (f.finalidade !== 'Compra' && f.finalidade !== 'Venda') return []
  const compra = f.finalidade === 'Compra'
  const r = calcular(f)
  const out: ContaFinanceira[] = []
  const base = { fichaId: f.id, numero: f.numero, data: f.data }
  const qs = d.quitacoes.filter((q) => q.fichaId === f.id)

  // 1. o vendedor (compra: a parte do gestor) ou o comprador (venda)
  if (r.temPreco) {
    const dv = divisaoDoPagamento(f)
    const valor = compra ? dv.doGestor : r.liquido
    const pg = situacaoDePagamento(f, valor, qs)
    if (valor > 0 || pg.pago > 0) {
      out.push(conta({
        ...base, id: `${compra ? 'vendedor' : 'comprador'}:${f.id}`, sentido: compra ? 'pagar' : 'receber', tipo: compra ? 'vendedor' : 'comprador',
        quem: f.produtor.nome || (compra ? 'vendedor' : 'comprador'),
        descricao: compra
          ? `Vendedor${dv.aCargoDoCliente ? ` (sua parte; ${dv.aCargoDoCliente.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })} a cargo do cliente)` : ''}`
          : 'Venda — o comprador paga o líquido do romaneio',
        valor, pago: pg.pago, vencimento: pg.proximoVencimento,
      }))
    }
  }

  // 2. frete e gastos: a parte do gestor (a da porteira Cliente o cliente paga direto)
  const doGestor = gastosDoGestor(f)
  for (const g of gastosValendo(f)) {
    const valor = doGestor.get(g.id) ?? g.valor
    if (!(valor > 0)) continue
    out.push(conta({
      ...base, id: `gasto:${f.id}:${g.id}`, sentido: 'pagar', tipo: 'gasto', gastoId: g.id, destinoId: g.destinoId,
      quem: g.favorecido?.trim() || g.descricao || 'gasto',
      descricao: `${g.descricao || 'Gasto'}${valor !== g.valor ? ` (sua parte de ${g.valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })})` : ''}`,
      valor, pago: g.pagoEm ? valor : 0, vencimento: g.vencimento || null,
    }))
  }

  if (!o.verComissao) return out

  // 3. Comissão do comprador (você paga): no acerto do extrato, ou fora dele (014)
  const c = d.comissoes?.[f.id]
  if (c && c.porCabeca > 0 && r.cab > 0) {
    const vc = valorDaComissao(c, r.cab)
    const fora = comissaoPagaFora(d, f.id)
    out.push(conta({
      ...base, id: `comissao:${f.id}`, sentido: 'pagar', tipo: 'comissaoComprador',
      quem: c.beneficiario?.trim() || f.pesadoPor || 'comprador',
      descricao: `${NOME_COMISSAO_COMPRADOR} — ${vc.cab} cab${c.acertoId ? ' · paga no acerto' : ''}`,
      valor: vc.valor, pago: c.acertoId ? vc.valor : fora, vencimento: null,
    }))
  }

  // 4. porteiras (só compra): Comissão do cliente (você recebe) e a revenda
  if (compra && porteirasDa(f).length) {
    const ccs = lista(d.comissoesCliente).filter((x) => x.fichaId === f.id)
    const vendas = lista(d.vendas).filter((x) => x.fichaId === f.id)
    const ap = contasPorPorteira(f, {
      verComissao: true, comissao: null,
      comissaoCliente: (destinoId) => ccs.find((x) => x.destinoId === destinoId) ?? null,
      vendaDe: (destinoId) => vendas.find((x) => x.destinoId === destinoId) ?? null,
    })
    for (const p of ap.porteiras) {
      if (!p.id) continue
      if (p.comissaoReceber && p.comissaoReceber.valor > 0) {
        out.push(conta({
          ...base, id: `comissao-cliente:${f.id}:${p.id}`, sentido: 'receber', tipo: 'comissaoCliente', destinoId: p.id,
          quem: p.nome, descricao: `${NOME_COMISSAO_CLIENTE} — ${p.comissaoReceber.cab} cab`,
          valor: p.comissaoReceber.valor, pago: comissaoRecebida(d, f.id, p.id), vencimento: null,
        }))
      }
      if (p.venda && p.venda.valor !== null && p.venda.valor > 0) {
        const sr = situacaoDoRecebimento(p.venda.valor, (d.recebimentos ?? []).filter((x) => x.fichaId === f.id && x.destinoId === p.id))
        out.push(conta({
          ...base, id: `revenda:${f.id}:${p.id}`, sentido: 'receber', tipo: 'revenda', destinoId: p.id,
          quem: p.porteira?.nome?.trim() || 'comprador final', descricao: `Revenda — ${p.cab} cab`,
          valor: p.venda.valor, pago: sr.recebido, vencimento: null,
        }))
      }
    }
  }
  return out
}

/** As contas de várias fichas (as excluídas ficam de fora). */
export function contasDasFichas(fichas: Ficha[], d: DadosFinanceiros, o: { verComissao: boolean }): ContaFinanceira[] {
  return fichas.filter((f) => f.situacao !== 'excluida').flatMap((f) => contasDaFicha(f, d, o))
}

export interface ResumoDasContas {
  aPagar: number
  aReceber: number
  /** quantas contas têm algo em aberto, de cada lado */
  nPagar: number
  nReceber: number
  /** pago ou recebido acima do devido — a tela avisa */
  aMais: number
  /** vencidas: o vencimento passou e ainda falta (sem vencimento não vence) */
  vencidas: number
}

/** Os totais da tela (Fichas, Contas) e da planilha: somados em centavos inteiros. */
export function resumirContas(cs: ContaFinanceira[], hoje = ''): ResumoDasContas {
  let p = 0, r = 0, np = 0, nr = 0, m = 0, v = 0
  for (const c of cs) {
    const f = emC(c.falta)
    if (c.sentido === 'pagar') { p += f; if (f) np++ } else { r += f; if (f) nr++ }
    m += emC(c.aMais)
    if (f && hoje && c.vencimento && c.vencimento < hoje) v++
  }
  return { aPagar: p / 100, aReceber: r / 100, nPagar: np, nReceber: nr, aMais: m / 100, vencidas: v }
}

/** A data em que a conta aparece na agenda: o vencimento, ou a data da ficha. */
export function dataDaConta(c: Pick<ContaFinanceira, 'vencimento' | 'data'>): string {
  return c.vencimento || c.data
}

/* ---------------------------------------------------------------------------
 * Parcelas: o combinado tem de bater com o que se paga (item 10 da revisão)
 * ------------------------------------------------------------------------- */

/**
 * «Parcelas somam X, líquido é Y — refazer?»: o aviso quando o combinado não
 * bate com o que o gestor deve (a parte dele, na intermediação). Nulo quando
 * bate, quando não há parcela ou quando não há preço.
 */
export function avisoDasParcelas(f: Pick<Ficha, 'pagamentos'>, devido: number): { soma: number; devido: number } | null {
  const ps = Array.isArray(f.pagamentos) ? f.pagamentos : []
  if (!ps.length || !(devido > 0)) return null
  const soma = ps.reduce((s, p) => s + emC(p.valor), 0)
  return soma === emC(devido) ? null : { soma: soma / 100, devido: centavos(devido) }
}

/** Há quitação valendo nesta ficha? Então as parcelas travam (o que foi pago cobre as parcelas pela ordem). */
export function parcelasTravadas(fichaId: string, quitacoes: Pick<Quitacao, 'fichaId' | 'anuladaEm' | 'recusada'>[]): boolean {
  return quitacoes.some((q) => q.fichaId === fichaId && !q.anuladaEm && !q.recusada)
}

/* ---------------------------------------------------------------------------
 * Total pago num dia — o limite diário do banco (tela Contas)
 * ------------------------------------------------------------------------- */

export interface PagoNoDia {
  total: number
  /** por «conta de origem» (vazio = não informada) */
  porConta: { conta: string; valor: number }[]
  /** quantos pagamentos */
  n: number
}

/**
 * O que SAIU no dia: pagamentos ao vendedor (quitações de ficha de Compra),
 * comissão do comprador paga fora do acerto e fretes e gastos com «pago em»
 * nesse dia (a parte do gestor). Recebimento não entra: o limite é de saída.
 */
export function pagoNoDia(fichas: Ficha[], d: DadosFinanceiros & { quitacoes: Pick<Quitacao, 'fichaId' | 'valor' | 'anuladaEm' | 'recusada' | 'pagoEm' | 'contaOrigem'>[] }, dia: string): PagoNoDia {
  const porConta = new Map<string, number>()
  let n = 0
  const somar = (conta: string | undefined, v: number) => {
    const k = conta?.trim() ?? ''
    porConta.set(k, (porConta.get(k) ?? 0) + emC(v)); n++
  }
  const compras = new Set(fichas.filter((f) => f.finalidade === 'Compra').map((f) => f.id))
  for (const q of d.quitacoes) if (!q.anuladaEm && !q.recusada && q.pagoEm === dia && compras.has(q.fichaId)) somar(q.contaOrigem, q.valor)
  for (const x of valendo(d.pagamentosComissao)) if (x.pagoEm === dia) somar(x.contaOrigem, x.valor)
  for (const f of fichas) {
    if (f.situacao === 'excluida') continue
    const doGestor = gastosDoGestor(f)
    for (const g of gastosValendo(f)) {
      const v = doGestor.get(g.id) ?? g.valor
      if (g.pagoEm === dia && v > 0) somar(g.contaOrigem, v)
    }
  }
  const lista = [...porConta.entries()].map(([conta, v]) => ({ conta, valor: v / 100 })).sort((a, b) => b.valor - a.valor)
  return { total: lista.reduce((s, x) => s + emC(x.valor), 0) / 100, porConta: lista, n }
}
